import { axiosInstance } from './axiosConfig';
import { handleRequestWithAuth } from './refreshService';

export interface Evento {
    id: number;
    titulo: string;
    subtitulo?: string;
    categoria: string;
    tipo: 'general' | 'networking';
    fecha: string;
    fecha_inicio?: string;
    hora?: string;
    frecuencia?: string;
    formato: string;
    capacidad: number;
    ubicacion: string;
    descripcion: string;
    detalles: string;
    estado: 'en curso' | 'proximo' | 'pasado' | 'cancelado';
    tags: string[];
    perks: string[];
    icono: string;
    color: string;
    color_dark?: string;
    gradiente?: string;
    imagen?: string | null;
    activo: boolean;
    orden: number;
    created_at?: string;
    updated_at?: string;
    total_inscritos: number;
    cupos_disponibles: number;
    porcentaje_ocupacion: number;
    is_registered?: boolean;
}

export interface InscripcionEvento {
    id: number;
    evento: number;
    evento_titulo?: string;
    evento_fecha?: string;
    evento_ubicacion?: string;
    usuario?: number | null;
    nombre_completo: string;
    email: string;
    telefono: string;
    empresa_o_cargo?: string;
    cupos: number;
    codigo_pase: string;
    estado: 'confirmado' | 'asistio' | 'cancelado';
    fecha_registro: string;
}

export interface InscribirsePayload {
    nombre_completo: string;
    email: string;
    telefono: string;
    empresa_o_cargo?: string;
    cupos?: number;
}

export interface InscribirseResponse {
    mensaje: string;
    inscripcion: InscripcionEvento;
    codigo_pase: string;
    total_inscritos: number;
    cupos_disponibles: number;
}

// ── Servicios Públicos (Landing Page) ──

export const fetchEventosPublicos = async (params?: {
    tipo?: string;
    estado?: string;
    search?: string;
}): Promise<Evento[]> => {
    const query = new URLSearchParams();
    if (params?.tipo) query.append('tipo', params.tipo);
    if (params?.estado && params.estado !== 'todos') query.append('estado', params.estado);
    if (params?.search) query.append('search', params.search);

    const url = `eventos-publicos/${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await axiosInstance.get(url);
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    return [];
};

export const inscribirseEvento = async (
    eventoId: number,
    payload: InscribirsePayload
): Promise<InscribirseResponse> => {
    const response = await axiosInstance.post(`eventos-publicos/${eventoId}/inscribirse/`, payload);
    return response.data;
};

// ── Servicios Administrativos (Panel Admin) ──

export const fetchEventosAdmin = async (params?: {
    tipo?: string;
    estado?: string;
}): Promise<Evento[]> => {
    return handleRequestWithAuth(async () => {
        const query = new URLSearchParams();
        if (params?.tipo) query.append('tipo', params.tipo);
        if (params?.estado && params.estado !== 'todos') query.append('estado', params.estado);

        const url = `eventos/${query.toString() ? `?${query.toString()}` : ''}`;
        const response = await axiosInstance.get(url);
        const data = response.data;
        if (Array.isArray(data)) return data;
        if (data && Array.isArray(data.results)) return data.results;
        return [];
    });
};

export const createEvento = async (data: Partial<Evento> | FormData): Promise<Evento> => {
    return handleRequestWithAuth(async () => {
        const isFormData = data instanceof FormData;
        const response = await axiosInstance.post('eventos/', data, {
            headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
        });
        return response.data;
    });
};

export const updateEvento = async (id: number, data: Partial<Evento> | FormData): Promise<Evento> => {
    return handleRequestWithAuth(async () => {
        const isFormData = data instanceof FormData;
        const response = await axiosInstance.patch(`eventos/${id}/`, data, {
            headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
        });
        return response.data;
    });
};

export const deleteEvento = async (id: number): Promise<void> => {
    return handleRequestWithAuth(async () => {
        await axiosInstance.delete(`eventos/${id}/`);
    });
};

export const fetchInscripcionesEvento = async (eventoId: number): Promise<InscripcionEvento[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`eventos/${eventoId}/inscripciones/`);
        return response.data;
    });
};
